import type { Profile, ProfilePatch } from '../../shared/api';
import { DEFAULT_AVATAR, MAX_NAME_LENGTH, isAvatar } from '../../shared/config';
import { QueryHttpError } from '../api/http';
import { issueProfile, readProfile, updateProfile } from '../api/profile';
import type { BrowserStore } from '../platform/storage';
import type { ProfileBlockFeatures } from '../../shared/profile-blocks';
import type { IdentityState } from './room-session-state';

/**
 * The browser's side of its server-owned profile: the credential that binds this browser
 * to the profile, a cache of the identity for synchronous first paint, and write-through
 * of local edits so the server has seen every change before the next room join.
 *
 * Browser storage is best-effort; a failed cache write never blocks play.
 */

export type IdentityPatch = Partial<Pick<IdentityState, 'name' | 'avatar'>>;

export interface ProfileSyncHooks {
  /** Identity or credential state changed; the session republishes its view. */
  changed(): void;
}

/** Cache keys are the historical `spellie.name`/`spellie.avatar`; the flag is new. */
const SYNCED_KEY = 'profileSynced';

export function createProfileSync(store: BrowserStore, identity: IdentityState, hooks: ProfileSyncHooks) {
  let inflight: Promise<void> | null = null;
  // Each local edit or room block notice advances the revision; older reads are discarded.
  let revision = 0;
  let loadRequest = 0;

  function setCredential(credential: string | null, persist = true): void {
    if (identity.profileCredential === credential) return;
    identity.profileCredential = credential;
    identity.profileGeneration++;
    if (persist) store.set('profileCredential', credential);
  }

  function cacheIdentity(): void {
    store.set('name', identity.name);
    store.set('avatar', identity.avatar);
    store.set(SYNCED_KEY, identity.profileSynced ? '1' : '0');
  }

  function applyProfile(profile: Profile): void {
    if (profile.name) identity.name = profile.name.slice(0, MAX_NAME_LENGTH);
    if (profile.avatar) identity.avatar = profile.avatar;
    identity.blockedUntil = profile.blockedUntil;
    identity.blockMessage = profile.blockedUntil === null ? null : profile.blockMessage;
    identity.blockedFeatures = profile.blockedUntil === null ? null : profile.blockedFeatures;
    identity.profileSynced = true;
    cacheIdentity();
    hooks.changed();
  }

  function localPatch(): ProfilePatch {
    return {
      ...(identity.name ? { name: identity.name } : {}),
      avatar: identity.avatar,
    };
  }

  async function issue(): Promise<void> {
    const startedAt = revision;
    const profile = await issueProfile(localPatch());
    setCredential(profile.credential);
    if (startedAt === revision) applyProfile(profile);
  }

  /** Push the local identity; a rejected credential is replaced by a fresh profile carrying it. */
  async function push(credential: string): Promise<void> {
    const startedAt = revision;
    try {
      const profile = await updateProfile(credential, localPatch());
      if (startedAt === revision && credential === identity.profileCredential) applyProfile(profile);
    } catch (error) {
      if (!(error instanceof QueryHttpError && error.status === 401)) return;
      if (credential !== identity.profileCredential) return;
      setCredential(null);
      await issue();
    }
  }

  async function ensureOnce(): Promise<void> {
    if (!identity.profileCredential) {
      const saved = store.get('profileCredential');
      if (saved) setCredential(saved, false);
    }
    if (!identity.profileCredential) {
      try {
        await issue();
      } catch {
        throw new Error('Could not connect to profile storage. Try again.');
      }
      return;
    }
    if (!identity.profileSynced) await push(identity.profileCredential);
  }

  function ensure(): Promise<void> {
    if (!inflight) {
      const startedAt = revision;
      inflight = ensureOnce().finally(() => {
        inflight = null;
        // An edit that landed mid-flight was not covered by it; flush it now so
        // rapid successive edits (name blur followed by an avatar pick) cannot
        // leave the later write unpushed. Unchanged revisions never loop: a
        // failed push without new edits still settles after one attempt.
        if (revision !== startedAt) void ensure().catch(() => undefined);
      });
    }
    return inflight;
  }

  return {
    /** Read the cached identity and credential; no network. */
    hydrate(): void {
      identity.name = (store.get('name') ?? '').trim().slice(0, MAX_NAME_LENGTH);
      const avatar = store.get('avatar');
      identity.avatar = isAvatar(avatar) ? avatar : DEFAULT_AVATAR;
      // A cached identity written before the flag existed has never reached the profile.
      identity.profileSynced = store.get(SYNCED_KEY) === '1' || !identity.name;
      setCredential(store.get('profileCredential'), false);
    },

    /**
     * Guarantee a credential and that the server holds the local identity. Issuance failure
     * throws; a failed write leaves the identity pending for the next call so the join can
     * still proceed with what the server last saw.
     */
    ensure,

    /**
     * Boot reconciliation: finish an edit left pending by a closed tab, otherwise refresh the
     * cache from the profile. A failed read never rejects a credential (join does that).
     */
    async load(): Promise<void> {
      const credential = identity.profileCredential;
      if (!credential) return;
      if (!identity.profileSynced) {
        await ensure().catch(() => undefined);
        return;
      }
      const startedAt = revision;
      const request = ++loadRequest;
      try {
        const profile = await readProfile(credential);
        if (request === loadRequest && startedAt === revision && credential === identity.profileCredential)
          applyProfile(profile);
      } catch {
        // The cache stands until a join or write settles the credential.
      }
    },

    /** Apply a local edit, cache it and write it through in the background. */
    setIdentity(patch: IdentityPatch): void {
      if (patch.name !== undefined) identity.name = patch.name.trim().slice(0, MAX_NAME_LENGTH);
      if (patch.avatar !== undefined) identity.avatar = patch.avatar;
      identity.profileSynced = false;
      revision++;
      cacheIdentity();
      hooks.changed();
      void ensure().catch(() => undefined);
    },

    setCredential,

    /** Apply the room's authoritative block notice without changing the credential. */
    setBlock(
      blockedUntil: number | null,
      blockMessage: string | null = null,
      blockedFeatures: ProfileBlockFeatures | null = null,
    ): void {
      revision++;
      identity.blockedUntil = blockedUntil;
      identity.blockMessage = blockedUntil === null ? null : blockMessage;
      identity.blockedFeatures = blockedUntil === null ? null : blockedFeatures;
      hooks.changed();
    },

    /** A late 401 for this exact credential invalidates it; a newer credential is untouched. */
    reject(credential: string): void {
      if (identity.profileCredential !== credential) return;
      setCredential(null);
      hooks.changed();
    },
  };
}
